/**
 * Copy and data the AI interior design landing sections share. Plain data
 * (no React), so scripts/test-interior-landing.ts can collect every string
 * the pages translate.
 */
import type { InteriorMode } from '@/constants/interiorDesigner';
import { roomThemesList } from '@/constants/roomThemes';

import { EXAMPLES, type LandingImage, roomImage } from './assets';
import { PAINT_PATH, STAGING_PATH } from './pages';
import type { LandingFaq } from './types';

export interface Capability {
  mode: InteriorMode;
  title: string;
  body: string;
  result: LandingImage;
  /** Small "before" picture in the corner. */
  before?: LandingImage;
  /** What was asked, shown as a speech bubble. */
  ask?: string;
  link?: { path: string; label: string };
}

export const CAPABILITIES: Capability[] = [
  {
    mode: 'restyle',
    title: 'Restyle the whole room',
    body: 'Pick a style and see the room redesigned, with the same walls, windows and layout.',
    result: roomImage('kitchen', 'after-3'),
    before: roomImage('kitchen', 'before'),
  },
  {
    mode: 'edit',
    title: 'Change one thing',
    body: 'Ask for a leather sofa, oak floors or a big plant. Everything else stays where it is.',
    result: EXAMPLES.editSofa,
    before: EXAMPLES.livingBefore,
    ask: 'Replace the sofa with a cognac leather sofa and add a fiddle leaf fig',
  },
  {
    mode: 'color-changer',
    title: 'Try a wall color',
    body: 'See a new color on your own walls before you buy paint. Nothing else changes.',
    result: EXAMPLES.paintTerracotta,
    before: EXAMPLES.bathroomBefore,
    link: { path: PAINT_PATH, label: 'Paint Color Visualizer' },
  },
  {
    mode: 'virtual-staging',
    title: 'Stage an empty room',
    body: 'Furnish an empty room in the style you choose, ready for a listing.',
    result: EXAMPLES.stagingStaged,
    before: EXAMPLES.stagingEmpty,
    link: { path: STAGING_PATH, label: 'AI Virtual Staging' },
  },
  {
    mode: 'empty-room',
    title: 'Clear the room',
    body: 'Take out the furniture to see the bare space, then design it again.',
    result: EXAMPLES.stagingEmpty,
    before: EXAMPLES.stagingFurnished,
  },
  {
    mode: 'generate',
    title: 'Start from a blank page',
    body: 'No photo? Describe the room you want and get a design from scratch.',
    result: EXAMPLES.createTropical,
    ask: 'A tropical living room with rattan chairs and a sea view',
  },
];

/** Questions every interior page answers, after the page's own. */
export const SHARED_FAQS: LandingFaq[] = [
  {
    question: 'Is it free to use?',
    answer:
      'Yes. Every account can make its first design free, and the Free plan includes a free design every month. Paid plans add more designs, 4K downloads and side-by-side style comparisons. No credit card is needed to sign up.',
  },
  {
    question: 'Will it keep my room’s layout?',
    answer:
      'Yes. Restyle and the other photo tools keep your walls, windows, doors and the shape of the room, and change the furniture, colors and decor. For a bigger change, describe it in your own words.',
  },
  {
    question: 'Which styles can I choose from?',
    answer:
      '{{styles}} styles, including Scandinavian, Mid-Century Modern, Japanese Zen, Industrial, Coastal, Bohemian, Farmhouse and Art Deco. Paid plans can compare up to 4 styles on the same photo at once.',
  },
  {
    question: 'What kind of photo works best?',
    answer:
      'A bright, level photo taken from a corner or doorway that shows as much of the room as possible. Phone photos are fine. Avoid strong filters, very dark shots and close-ups.',
  },
  {
    question: 'Can I change just one thing?',
    answer:
      'Yes. Use Edit and write what to change, like “replace the rug with a jute rug” or “paint the cabinets navy”. You can also mark spots on the photo and describe each one.',
  },
  {
    question: 'Can I use it for real estate listings?',
    answer:
      'Yes. Virtual staging furnishes empty rooms, and Empty room clears out old furniture first. Many listing services ask you to label edited photos as virtually staged, so check the rules where you list.',
  },
  {
    question: 'Are my photos private?',
    answer:
      'Yes. Your photos and designs are private to your account unless you share them, and we don’t use them to train our own models.',
  },
];

/** Furnished listing photo, emptied by AI, then staged: one real sequence. */
export const STAGING_STEPS = [
  { image: EXAMPLES.stagingFurnished, label: 'The listing photo' },
  { image: EXAMPLES.stagingEmpty, label: 'Emptied with Empty room' },
  { image: EXAMPLES.stagingStaged, label: 'Staged in a Scandinavian style' },
];

/** What paid plans add (besides more designs). */
export const PLAN_PERKS = [
  '4K downloads',
  'Up to 8 designs in one go',
  'Compare up to 4 styles at once',
  'Images, videos and AI tools beyond interior design',
];

/** Spaces linked (with their designs) from the landscape and exterior pages. */
export const SPACE_GROUPS = {
  outdoor: [
    'backyard',
    'front-yard',
    'outdoor-garden',
    'outdoor-patio',
    'deck',
    'outdoor-pool-area',
    'balcony',
    'rooftop-terrace',
    'outdoor-kitchen',
    'covered-porch',
    'greenhouse',
    'swimming-pool',
  ],
  exterior: [
    'front-yard',
    'covered-porch',
    'outdoor-garage',
    'carport',
    'backyard',
    'deck',
    'outdoor-patio',
    'balcony',
  ],
};

/** Styles people know best, shown first. */
const POPULAR_STYLES = [
  'japandi',
  'scandinavian',
  'minimalist',
  'mid-century-modern',
  'japanese-zen',
  'industrial',
  'bohemian',
  'farmhouse',
  'coastal',
  'art-deco',
  'mediterranean',
  'contemporary',
  'rustic',
];

/** Styles in the designer's picker ("Custom" lets you describe your own). */
export const INTERIOR_STYLES = roomThemesList
  .filter((theme) => theme.slug !== 'custom')
  .map((theme, index) => ({ theme, index }))
  .sort((a, b) => {
    const rank = (slug: string) => {
      const found = POPULAR_STYLES.indexOf(slug);
      return found < 0 ? POPULAR_STYLES.length : found;
    };
    return rank(a.theme.slug) - rank(b.theme.slug) || a.index - b.index;
  })
  .map(({ theme }) => theme);
export const INTERIOR_STYLE_COUNT = INTERIOR_STYLES.length;
